import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Shield, Mail, Info, Loader } from 'lucide-react';
import { apiGet } from '../utils/api';
import UserMenu from '../components/UserMenu';

/**
 * "Your Profile" — read-only staff profile screen.
 *
 * Data comes from GET /api/profile/me, which returns the raw Mongo user
 * document. The fields below are an explicit whitelist — nothing spreads
 * the response into the UI.
 *
 * Staff profiles are read-only because identity is mastered in Microsoft
 * 365 / Entra ID and re-synced from Azure AD claims on every sign-in, so
 * any local edit would be overwritten anyway — there is no PATCH endpoint
 * for staff.
 */

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const val = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// One labelled row in the details card.
const Field = ({ label, children }) => (
  <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{children}</dd>
  </div>
);

const UserProfile = ({ navigateTo }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/profile/me');
        if (!res || !res.ok) {
          if (!cancelled) setError(res ? `Could not load profile (${res.status})` : 'Session expired');
          return;
        }
        const data = await res.json();
        if (!cancelled) setProfile(data);
      } catch (e) {
        if (!cancelled) setError('Could not load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateTo('home')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Your Profile</h1>
                <p className="text-sm text-gray-500">Account details</p>
              </div>
            </div>
            <UserMenu navigateTo={navigateTo} />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading profile…</span>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && profile && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-start space-x-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                Staff details are managed in Microsoft 365 / Entra ID and re-sync from Azure AD
                on each sign-in, so they cannot be edited here.
              </p>
            </div>

            <div className="px-6 py-5">
              <div className="flex items-center space-x-3 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center">
                  <User className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{val(profile.name)}</h2>
                  <p className="text-sm text-gray-500">{val(profile.email)}</p>
                </div>
              </div>

              <dl className="divide-y divide-gray-100">
                <Field label="Name">{val(profile.name)}</Field>
                <Field label="Email">
                  <span className="inline-flex items-center space-x-1">
                    <Mail className="h-3.5 w-3.5 text-gray-400" />
                    <span>{val(profile.email)}</span>
                  </span>
                </Field>
                <Field label="Roles">
                  {Array.isArray(profile.roles) && profile.roles.length > 0 ? (
                    <span className="flex flex-wrap gap-1">
                      {profile.roles.map((r) => (
                        <span key={r} className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                          {r}
                        </span>
                      ))}
                    </span>
                  ) : '—'}
                </Field>
                <Field label="Last sign-in">{fmtDate(profile.last_login)}</Field>
                <Field label="Account status">
                  {profile.is_active === false ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">Inactive</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">Active</span>
                  )}
                </Field>
              </dl>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
